/**
 * Permissions Matrix — MVP RBAC
 *
 * Modules: cases, schedule, shift_plan, customers, quotes, invoices, receipts,
 *          payroll, accounting, reports, users
 *
 * Actions: read, create, update, approve, export
 *
 * Default deny: if a role is not listed, it has no access.
 */

import type { SystemRole } from '@/types/index';

export type PermissionModule =
  | 'cases'
  | 'schedule'
  | 'shift_plan'
  | 'customers'
  | 'quotes'
  | 'invoices'
  | 'receipts'
  | 'payroll'
  | 'accounting'
  | 'reports'
  | 'users'
  | 'mileage'
  | 'bug_reports'
  | 'system_health'
  | 'warehouse'
  | 'message_templates'
  | 'teams';

export type PermissionAction = 'read' | 'create' | 'update' | 'approve' | 'export';

type RolePermissions = Partial<Record<PermissionModule, PermissionAction[]>>;

// ─── Full matrix ──────────────────────────────────────────
const PERMISSIONS_MATRIX: Record<SystemRole, RolePermissions> = {
  admin: {
    cases:       ['read', 'create', 'update', 'approve', 'export'],
    schedule:    ['read', 'create', 'update', 'approve'],
    shift_plan:  ['read', 'create', 'update', 'approve'],
    customers:   ['read', 'create', 'update', 'export'],
    quotes:      ['read', 'create', 'update', 'approve', 'export'],
    invoices:    ['read', 'create', 'update', 'approve', 'export'],
    receipts:    ['read', 'create', 'update', 'approve', 'export'],
    payroll:     ['read', 'create', 'update', 'approve', 'export'],
    accounting:  ['read', 'create', 'update', 'approve', 'export'],
    reports:     ['read', 'create', 'update', 'approve', 'export'],
    users:       ['read', 'create', 'update'],
    mileage:     ['read', 'create', 'update', 'approve', 'export'],
    bug_reports: ['read', 'create', 'update'],
    system_health: ['read', 'update'],
    warehouse: ['read', 'create', 'update', 'approve', 'export'],
    message_templates: ['read', 'create', 'update'],
    teams: ['read', 'create', 'update', 'approve'],
  },
  supervisor: {
    cases:       ['read', 'create', 'update', 'approve'],
    schedule:    ['read', 'create', 'update', 'approve'],
    shift_plan:  ['read', 'create', 'update', 'approve'],
    customers:   ['read', 'create', 'update'],
    quotes:      ['read', 'create', 'update'],
    invoices:    ['read'],
    receipts:    ['read'],
    reports:     ['read'],
    mileage:     ['read', 'approve'],
    bug_reports: ['read', 'update'],
    warehouse: ['read', 'create', 'update', 'approve'],
    message_templates: ['read', 'create', 'update'],
    teams: ['read', 'create', 'update'],
  },
  founder: {
    cases:       ['read', 'create', 'update', 'approve', 'export'],
    schedule:    ['read', 'create', 'update', 'approve'],
    shift_plan:  ['read', 'create', 'update', 'approve'],
    customers:   ['read', 'create', 'update', 'export'],
    quotes:      ['read', 'create', 'update', 'approve', 'export'],
    invoices:    ['read', 'create', 'update', 'approve', 'export'],
    receipts:    ['read', 'create', 'update', 'approve', 'export'],
    payroll:     ['read', 'create', 'update', 'approve', 'export'],
    accounting:  ['read', 'create', 'update', 'approve', 'export'],
    reports:     ['read', 'create', 'update', 'approve', 'export'],
    users:       ['read', 'create', 'update'],
    mileage:     ['read', 'create', 'update', 'approve', 'export'],
    bug_reports: ['read', 'create', 'update'],
    system_health: ['read', 'update'],
    warehouse: ['read', 'create', 'update', 'approve', 'export'],
    message_templates: ['read', 'create', 'update'],
    teams: ['read', 'create', 'update', 'approve'],
  },
  backoffice: {
    cases:       ['read', 'create', 'update'],
    schedule:    ['read', 'create', 'update'],
    customers:   ['read', 'create', 'update'],
    quotes:      ['read', 'create', 'update'],
    invoices:    ['read', 'create', 'update'],
    receipts:    ['read', 'create', 'update'],
    warehouse:   ['read', 'create', 'update'],
  },
  field_worker: {
    cases:       ['read'],
    schedule:    ['read'],
    shift_plan:  ['read'],
    mileage:     ['read', 'create'],
    warehouse:   ['read'],
    teams:       ['read'],
  },
  accounting: {
    cases:       ['read'],
    quotes:      ['read', 'export'],
    invoices:    ['read', 'create', 'update', 'approve', 'export'],
    receipts:    ['read', 'create', 'update', 'approve', 'export'],
    payroll:     ['read', 'create', 'update', 'approve', 'export'],
    accounting:  ['read', 'create', 'update', 'approve', 'export'],
    reports:     ['read', 'export'],
    mileage:     ['read', 'export'],
  },
  auditor: {
    cases:       ['read'],
    quotes:      ['read'],
    invoices:    ['read', 'export'],
    receipts:    ['read', 'export'],
    accounting:  ['read', 'export'],
    reports:     ['read', 'export'],
    mileage:     ['read', 'export'],
  },
};

// ─── Helpers ──────────────────────────────────────────────

/** Check if a role has a specific action on a module */
export function hasPermission(
  role: SystemRole | null | undefined,
  module: PermissionModule,
  action: PermissionAction = 'read',
): boolean {
  if (!role) return false;
  const perms = PERMISSIONS_MATRIX[role]?.[module];
  if (!perms) return false;
  return perms.includes(action);
}

/** Check if a role can access a module at all (has read) */
export function canAccessModule(
  role: SystemRole | null | undefined,
  module: PermissionModule,
): boolean {
  return hasPermission(role, module, 'read');
}

/** Get all permissions for a role */
export function getRolePermissions(role: SystemRole): RolePermissions {
  return PERMISSIONS_MATRIX[role] || {};
}

/** Get the full matrix (for admin display) */
export function getFullMatrix() {
  return PERMISSIONS_MATRIX;
}

// ─── Module metadata (for UI display) ────────────────────
export const MODULE_LABELS: Record<PermissionModule, string> = {
  cases:       'Sager',
  schedule:    'Planlægning',
  shift_plan:  'Vagtplan',
  customers:   'Kunder',
  quotes:      'Tilbud',
  invoices:    'Fakturaer',
  receipts:    'Bilag',
  payroll:     'Løn',
  accounting:  'Bogføring',
  reports:     'Rapporter',
  users:       'Brugere',
  mileage:     'Kørsel',
  bug_reports: 'Fejlrapporter',
  system_health: 'Fejlovervågning',
  warehouse: 'Lagerstyring',
  message_templates: 'Beskedskabeloner',
  teams: 'Teams',
};

export const ACTION_LABELS: Record<PermissionAction, string> = {
  read:    'Læs',
  create:  'Opret',
  update:  'Rediger',
  approve: 'Godkend',
  export:  'Eksportér',
};

export const ALL_MODULES: PermissionModule[] = [
  'cases', 'schedule', 'shift_plan', 'customers', 'quotes',
  'invoices', 'receipts', 'payroll', 'accounting', 'reports', 'users', 'mileage', 'bug_reports', 'system_health', 'warehouse', 'message_templates', 'teams',
];

export const ALL_ACTIONS: PermissionAction[] = ['read', 'create', 'update', 'approve', 'export'];
